/**
 * Installation de l’app de contrôle sur le téléphone (SCAN-01, « PWA »).
 *
 * Ce que ce module fait :
 *  - déclare le manifeste et enregistre le service worker **s’ils sont servis**
 *    à la racine du site (`/manifest.webmanifest`, `/sw.js`) ;
 *  - expose l’invite d’installation d’Android/Chrome, et les instructions iOS ;
 *  - passe la couleur de barre système au sombre pendant la soirée.
 *
 * `frontend/public/manifest.webmanifest`, `frontend/public/sw.js` et les trois
 * icônes (`icone-scan-192.png`, `icone-scan-512.png`, `icone-scan-maskable.png`)
 * sont des copies exactes des sources de `features/scan/pwa/` — Vite sert tout
 * `public/` tel quel, à la racine (SCAN-01). `ressourceServie` reste
 * volontairement défensif : si ces fichiers venaient à manquer (déploiement
 * partiel, CDN qui les filtre), l’app de scan continue de fonctionner à
 * l’identique — seule l’installation sur l’écran d’accueil serait indisponible.
 *
 * ★ **HLG-01 — le service worker sert la coquille, et RIEN d’autre.** Il met en
 * cache le HTML, le JS et le CSS, pour que l’app démarre sans réseau ; **aucune**
 * réponse `/api` n’est cachée, et ce point n’est pas négociable : une réponse
 * d’API rejouée depuis un cache donnerait un verdict faux sans que personne ne
 * le sache. La matière du mode hors ligne ne passe donc jamais par le cache HTTP
 * — elle est téléchargée DÉLIBÉRÉMENT (HLG-06) par `horsligne/`, horodatée
 * (HLG-03), et l’écran dit en permanence de quand elle date.
 */

import { useCallback, useEffect, useState } from 'react'

const CHEMIN_MANIFESTE = '/manifest.webmanifest'
const CHEMIN_SERVICE_WORKER = '/sw.js'

/** Fond de l’espace de scan (`--color-slate-950`), pour la barre système. */
const COULEUR_BARRE = '#020617'

/**
 * Une SPA renvoie `index.html` en 200 pour toute URL inconnue : un simple
 * `response.ok` ne prouve donc pas que le fichier existe. On vérifie le type.
 */
async function ressourceServie(chemin: string, typeAttendu: string): Promise<boolean> {
  try {
    const reponse = await fetch(chemin, { method: 'GET', cache: 'no-store' })
    if (!reponse.ok) return false
    return (reponse.headers.get('content-type') ?? '').includes(typeAttendu)
  } catch {
    return false
  }
}

/**
 * Branche le manifeste et le service worker, et assombrit la barre système
 * tant que l’écran de scan est monté. À appeler une fois, dans la coque de la
 * feature.
 */
export function useConfigurationPwa(): void {
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]')
    const couleurPrecedente = meta?.getAttribute('content') ?? null
    meta?.setAttribute('content', COULEUR_BARRE)

    let annule = false

    const brancher = async (): Promise<void> => {
      if (document.querySelector('link[rel="manifest"]') === null) {
        if (await ressourceServie(CHEMIN_MANIFESTE, 'json')) {
          if (annule) return
          const lien = document.createElement('link')
          lien.rel = 'manifest'
          lien.href = CHEMIN_MANIFESTE
          document.head.append(lien)
        }
      }

      if (!('serviceWorker' in navigator)) return
      if (!(await ressourceServie(CHEMIN_SERVICE_WORKER, 'javascript'))) return
      if (annule) return
      try {
        const inscription = await navigator.serviceWorker.register(CHEMIN_SERVICE_WORKER, {
          scope: '/',
          updateViaCache: 'none',
        })
        await inscription.update()
      } catch {
        // Un service worker qui refuse de s’installer ne doit jamais empêcher
        // la soirée : le scan fonctionne sans lui.
      }
    }

    void brancher()

    return () => {
      annule = true
      if (couleurPrecedente !== null) meta?.setAttribute('content', couleurPrecedente)
    }
  }, [])
}

/* -------------------------------------------------------------------------- */
/* Invite d’installation                                                      */
/* -------------------------------------------------------------------------- */

interface EvenementInstallation extends Event {
  readonly prompt: () => Promise<void>
  readonly userChoice: Promise<{ readonly outcome: 'accepted' | 'dismissed' }>
}

export interface UtiliseInstallation {
  /** L’app tourne déjà depuis l’écran d’accueil. */
  readonly installee: boolean
  /** Le navigateur propose l’installation en un geste (Android/Chrome). */
  readonly installable: boolean
  /** iOS : pas d’invite, il faut expliquer « Partager → Sur l’écran d’accueil ». */
  readonly instructionsIos: boolean
  readonly installer: () => void
}

function enModeApplication(): boolean {
  if (typeof window === 'undefined') return false
  const autonome = (navigator as unknown as { standalone?: boolean }).standalone === true
  return autonome || window.matchMedia('(display-mode: standalone)').matches
}

function estIos(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  // iPadOS 13+ se présente comme un Mac : le test tactile lève l’ambiguïté.
  return /iphone|ipad|ipod/iu.test(ua) || (/macintosh/iu.test(ua) && navigator.maxTouchPoints > 1)
}

export function useInstallationPwa(): UtiliseInstallation {
  const [invite, setInvite] = useState<EvenementInstallation | null>(null)
  const [installee, setInstallee] = useState(enModeApplication)

  useEffect(() => {
    const surInvite = (evenement: Event): void => {
      evenement.preventDefault()
      setInvite(evenement as EvenementInstallation)
    }
    const surInstallation = (): void => {
      setInstallee(true)
      setInvite(null)
    }
    window.addEventListener('beforeinstallprompt', surInvite)
    window.addEventListener('appinstalled', surInstallation)
    return () => {
      window.removeEventListener('beforeinstallprompt', surInvite)
      window.removeEventListener('appinstalled', surInstallation)
    }
  }, [])

  const installer = useCallback(() => {
    if (invite === null) return
    void invite.prompt().then(async () => {
      const choix = await invite.userChoice
      if (choix.outcome === 'accepted') setInstallee(true)
      setInvite(null)
    })
  }, [invite])

  return {
    installee,
    installable: invite !== null,
    instructionsIos: !installee && invite === null && estIos(),
    installer,
  }
}

/* -------------------------------------------------------------------------- */
/* État du réseau                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Sans liste préparée, le scan **exige** le réseau (SCAN-06) : cet indicateur
 * sert à le dire tout de suite, avant que l’opérateur ne présente trois billets
 * dans le vide. Avec une liste préparée (HLG-06), c’est lui qui fait basculer
 * l’écran en mode hors ligne — et qui déclenche la remontée au retour.
 */
export function useReseau(): boolean {
  const [enLigne, setEnLigne] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  )

  useEffect(() => {
    const monter = (): void => {
      setEnLigne(true)
    }
    const tomber = (): void => {
      setEnLigne(false)
    }
    window.addEventListener('online', monter)
    window.addEventListener('offline', tomber)
    return () => {
      window.removeEventListener('online', monter)
      window.removeEventListener('offline', tomber)
    }
  }, [])

  return enLigne
}
